//@flow
import React from 'react';
import classnames from 'classnames';
import { themr } from 'react-css-themr';
import inputTheme from './InputWithRenderer.scss';
import { Input } from 'common/components/core/input/Input';
import StringUtils from 'common/utils/StringUtils';
import compose from 'recompose/compose';
import withHandlers from 'recompose/withHandlers';
import withStateHandlers from 'recompose/withStateHandlers';

type InputPropTypes = {
    theme: Object,
    className?: string | any,
    isFocused: boolean,
    onFocus?: () => void,
    wrapperClassName?: string,
    inputValue?: string | any,
    disabled: boolean,
    readOnly?: boolean,
    setIsFocused: Function
};

type InputRendererParams = {
    theme: Object,
    value?: string | any,
    inputValue?: string | any,
    renderTitle?: boolean,
    unit?: string,
    isFocused: boolean,
    placeholder?: string
};
const DefaultInputRenderer = ({
    theme,
    value,
    renderTitle,
    unit,
    isFocused,
    placeholder,
    semiboldValue
}: InputRendererParams) => {
    return !isFocused ? (
        <div className={theme.inputRenderer}>
            {!StringUtils.isEmpty(value) ? (
                <div
                    className={classnames(theme.renderValueWrap, {
                        [theme.semiboldValue]: semiboldValue
                    })}
                >
                    {!StringUtils.isEmpty(renderTitle) ? (
                        <span className={theme.renderTitle}>
                            {renderTitle}
                            &nbsp;
                        </span>
                    ) : null}
                    <span className={theme.renderValue}>{value}</span>
                    {!StringUtils.isEmpty(unit) ? (
                        <span className={theme.renderUnit}>
                            &nbsp;
                            {unit}
                        </span>
                    ) : null}
                </div>
            ) : (
                <div className={theme.placeholder}>{placeholder}</div>
            )}
        </div>
    ) : null;
};

const inputWithRendererFactory = ({
    InputRenderer = DefaultInputRenderer,
    InputComponent = Input
}) => {
    class InputWithRenderer extends React.PureComponent<InputPropTypes> {
        render() {
            const {
                theme,
                className,
                isFocused,
                inputValue,
                disabled,
                setIsFocused, //eslint-disable-line no-unused-vars,
                wrapperClassName,
                readOnly,
                ...others
            } = this.props;
            return (
                <div
                    className={classnames(
                        theme.inputWithRenderer,
                        wrapperClassName,
                        {
                            [theme.disabledRenderer]:
                                (disabled || readOnly) && theme.disabledRenderer
                        }
                    )}
                >
                    <InputComponent
                        {...others}
                        disabled={disabled}
                        readOnly={readOnly}
                        theme={theme}
                        isFocused={isFocused}
                        className={classnames({
                            [className]: className,
                            [theme.isFocused]: isFocused
                        })}
                    />
                    <InputRenderer
                        isFocused={isFocused}
                        theme={theme}
                        inputValue={inputValue}
                        {...others}
                    />
                </div>
            );
        }
    }
    return themr('InputWithRenderer', inputTheme, { withRef: true })(
        InputWithRenderer
    );
};

const InputRenderStateHandlers = compose(
    withStateHandlers(
        {
            isFocused: false
        },
        {
            setIsFocused: () => isFocused => {
                return {
                    isFocused
                };
            }
        }
    ),
    withHandlers({
        onFocus: ({ onFocus, setIsFocused }) => (...args) => {
            setIsFocused(true);
            onFocus && onFocus(...args);
        },
        onBlur: ({ onBlur, setIsFocused }) => (...args) => {
            setIsFocused(false);
            onBlur && onBlur(...args);
        },
        onKeyDown: ({ onEnter }) => (...args) => {
            const [event] = args;
            if (event.keyCode === 13) {
                onEnter && onEnter(...args);
            }
        }
    })
);
const ThemedInputWithRenderer = inputWithRendererFactory({});
export default ThemedInputWithRenderer;
export {
    inputWithRendererFactory,
    ThemedInputWithRenderer as InputWithRenderer,
    InputRenderStateHandlers,
    DefaultInputRenderer
};
